import { useState } from "react";
import { useSelector } from "react-redux";
import { NavLink, Navigate, Outlet } from "react-router-dom";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { BreadcrumbAdmin } from "../../UI/BreadcrumbAdmin";
import { useAlert } from "../../../hooks/useAlert";
import { selectCurrentUser } from "../../../redux-toolkit/features/auth/authSlice";
import {
  useCreateItUserMutation,
  useDeleteItUserMutation,
  useGetItUsersQuery,
  useSetItUserActiveMutation,
  useSetItUserPasswordMutation,
} from "../../../redux-toolkit/features/itUsers/itUsersApiSlice";
import "./PlatformSettings.css";

const SECTIONS = [{ label: "IT Users", to: "it-users" }];

export const ItOnlyRoute = () => {
  const user = useSelector(selectCurrentUser);
  if (user?.role !== "IT") {
    return <Navigate to="/restricted" replace />;
  }
  return <Outlet />;
};

export const PlatformSettings = () => (
  <div className="platformSettings">
    <BreadcrumbAdmin
      base={[{ _id: 1, link: "/super-admin", label: "Dashboard" }]}
      page="Platform Settings"
    />
    <nav className="platformSettings__nav">
      {SECTIONS.map((section) => (
        <NavLink key={section.to} to={section.to}>
          {section.label}
        </NavLink>
      ))}
    </nav>
    <Outlet />
  </div>
);

const errorMessage = (error, fallback) =>
  error?.data?.message || fallback;

const EMPTY_IT_USER = {
  fname: "",
  lname: "",
  email: "",
  password: "",
};

const passwordError = (value) => {
  if (!value || value.length < 8) return "Password must be at least 8 characters";
  if (!/[0-9]/.test(value)) return "Password must contain at least one number";
  if (!/[^A-Za-z0-9]/.test(value)) {
    return "Password must contain at least one special character";
  }
  return "";
};

export const ItUsers = () => {
  const currentUser = useSelector(selectCurrentUser);
  const { displayAlert } = useAlert();
  const { data, isLoading, isError } = useGetItUsersQuery();
  const [setActive, { isLoading: isUpdating }] = useSetItUserActiveMutation();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteItUserMutation();
  const [setPassword, { isLoading: isSavingPassword }] =
    useSetItUserPasswordMutation();
  const [createUser, { isLoading: isCreating }] = useCreateItUserMutation();
  const [passwordFor, setPasswordFor] = useState(null);
  const [password, setPasswordValue] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_IT_USER);

  const users = data?.data || [];
  const busy = isUpdating || isDeleting || isSavingPassword || isCreating;
  const formReady =
    form.fname.trim() &&
    form.lname.trim() &&
    form.email.trim() &&
    !passwordError(form.password);
  const closeAdd = () => {
    setAdding(false);
    setForm(EMPTY_IT_USER);
  };
  const closePassword = () => {
    setPasswordFor(null);
    setPasswordValue("");
  };

  const run = async (action, success) => {
    try {
      await action().unwrap();
      displayAlert({ open: true, message: success, type: "success", timeout: "2500" });
    } catch (error) {
      displayAlert({
        open: true,
        message: errorMessage(error, "Something went wrong"),
        type: "error",
        timeout: "2500",
      });
    }
  };

  const savePassword = async () => {
    try {
      await setPassword({ id: passwordFor._id, password }).unwrap();
      displayAlert({
        open: true,
        message: "Password updated",
        type: "success",
        timeout: "2500",
      });
      closePassword();
    } catch (error) {
      displayAlert({
        open: true,
        message: errorMessage(error, "Could not update password"),
        type: "error",
        timeout: "2500",
      });
    }
  };

  const saveUser = async () => {
    try {
      await createUser(form).unwrap();
      displayAlert({
        open: true,
        message: "IT user added",
        type: "success",
        timeout: "2500",
      });
      closeAdd();
    } catch (error) {
      displayAlert({
        open: true,
        message: errorMessage(error, "Could not add IT user"),
        type: "error",
        timeout: "2500",
      });
    }
  };

  if (isLoading) {
    return <p>Loading IT users...</p>;
  }

  if (isError) {
    return <p>Could not load IT users.</p>;
  }

  return (
    <>
      <div className="platformSettings__toolbar">
        <Button
          variant="contained"
          size="small"
          disabled={busy}
          onClick={() => {
            closeAdd();
            setAdding(true);
          }}
        >
          Add IT user
        </Button>
      </div>
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.length === 0 ? (
            <tr>
              <td colSpan={4}>No IT users</td>
            </tr>
          ) : (
            users.map((user) => {
              const isSelf = String(user._id) === String(currentUser?._id);
              return (
                <tr key={user._id}>
                  <td>{`${user.fname || ""} ${user.lname || ""}`.trim()}</td>
                  <td>{user.email}</td>
                  <td>{user.isActive ? "Active" : "Disabled"}</td>
                  <td>
                    <div className="platformSettings__actions">
                      {isSelf ? null : (
                        <Button
                          size="small"
                          disabled={busy}
                          onClick={() =>
                            run(
                              () =>
                                setActive({
                                  id: user._id,
                                  isActive: !user.isActive,
                                }),
                              user.isActive ? "User disabled" : "User enabled",
                            )
                          }
                        >
                          {user.isActive ? "Disable" : "Enable"}
                        </Button>
                      )}
                      {isSelf ? null : (
                        <Button
                          size="small"
                          color="error"
                          disabled={busy}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Delete ${user.email}? This erases their personal data.`,
                              )
                            ) {
                              run(() => deleteUser(user._id), "User deleted");
                            }
                          }}
                        >
                          Delete
                        </Button>
                      )}
                      <Button
                        size="small"
                        disabled={busy}
                        onClick={() => {
                          setPasswordFor(user);
                          setPasswordValue("");
                        }}
                      >
                        Set password
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      <Dialog open={adding} onClose={closeAdd}>
        <DialogTitle>Add IT user</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="First name"
            fullWidth
            value={form.fname}
            onChange={(event) =>
              setForm((current) => ({ ...current, fname: event.target.value }))
            }
          />
          <TextField
            margin="dense"
            label="Last name"
            fullWidth
            value={form.lname}
            onChange={(event) =>
              setForm((current) => ({ ...current, lname: event.target.value }))
            }
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={form.email}
            onChange={(event) =>
              setForm((current) => ({ ...current, email: event.target.value }))
            }
          />
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            value={form.password}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                password: event.target.value,
              }))
            }
            helperText={
              form.password && passwordError(form.password)
                ? passwordError(form.password)
                : "At least 8 characters, a number, and a special character."
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAdd}>Cancel</Button>
          <Button disabled={busy || !formReady} onClick={saveUser}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(passwordFor)}
        onClose={closePassword}
      >
        <DialogTitle>Set password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New password"
            type="password"
            fullWidth
            value={password}
            onChange={(event) => setPasswordValue(event.target.value)}
            helperText={
              password && passwordError(password)
                ? passwordError(password)
                : "At least 8 characters, a number, and a special character."
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closePassword}>Cancel</Button>
          <Button disabled={busy || Boolean(passwordError(password))} onClick={savePassword}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
